import React, { useEffect, useMemo } from 'react';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useHistory } from '@shared/core';
import { useRouterHelper } from '@shared/core/router.helper';
import { useQueryParamHelper } from '@shared/core/queryString';
import { routePaths } from '@apps/acquisitionFunnels/createWedding/route.paths';
import { getUserScenario } from '@apps/acquisitionFunnels/createWedding/utils';
import type { CreateWeddingGuardsProps } from '@apps/acquisitionFunnels/createWedding/CreateWedding.types';

/**
 * Combined auth guard for CreateWedding routes.
 *
 * - authMode="allowOnlyAuthenticated": Only allows logged-in users (redirects to /create-account if not logged in)
 * - authMode="allowOnlyUnauthenticated": Only allows logged-out users (redirects to /createwedding if logged in)
 * - authMode="requireJoyOrNonSocialAuthenticated": Only allows Joy account users (redirects social login users to /social-account)
 */
export const CreateWeddingGuards: React.FC<CreateWeddingGuardsProps> = ({ children, authMode, placeholder = null }) => {
  const auth = useAuth();
  const { eventMemberships, loading: isLoadingEventMemberships } = useEventMemberships();
  const history = useHistory();
  const routeHelpers = useRouterHelper();
  const queryParamHelper = useQueryParamHelper();

  const { isCurrentUserSettled, isLoggedIn } = auth;

  const needsMemberships = authMode === 'requireJoyOrNonSocialAuthenticated';

  const userScenario = useMemo(() => (needsMemberships ? getUserScenario(auth, eventMemberships) : null), [needsMemberships, auth, eventMemberships]);

  const isReady = needsMemberships ? isCurrentUserSettled && !isLoadingEventMemberships : isCurrentUserSettled;

  const shouldRedirectToCreateAccount = (authMode === 'allowOnlyAuthenticated' && !isLoggedIn) || (authMode === 'requireJoyOrNonSocialAuthenticated' && !isLoggedIn);

  const shouldRedirectToCreateWedding = authMode === 'allowOnlyUnauthenticated' && isLoggedIn;

  const shouldRedirectToSocialAccount = authMode === 'requireJoyOrNonSocialAuthenticated' && isLoggedIn && userScenario === 'socialLogin';

  useEffect(() => {
    if (!isReady) {
      return;
    }

    switch (authMode) {
      case 'allowOnlyAuthenticated': {
        if (shouldRedirectToCreateAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createAccount.path));
          history.replace(path);
        }
        break;
      }

      case 'allowOnlyUnauthenticated': {
        if (shouldRedirectToCreateWedding) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createWeddingForm.path));
          history.replace(path);
        }
        break;
      }

      case 'requireJoyOrNonSocialAuthenticated': {
        if (shouldRedirectToCreateAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createAccount.path));
          history.replace(path);
        } else if (shouldRedirectToSocialAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.socialAccount.path));
          history.replace(path);
        }
        break;
      }

      default:
        break;
    }
  }, [authMode, isReady, shouldRedirectToCreateAccount, shouldRedirectToCreateWedding, shouldRedirectToSocialAccount, history, routeHelpers, queryParamHelper]);

  const shouldRedirect = shouldRedirectToCreateAccount || shouldRedirectToCreateWedding || shouldRedirectToSocialAccount;

  if (!isReady || shouldRedirect) {
    return <>{placeholder}</>;
  }

  return <>{children}</>;
};
